/**
 * The viewport as it actually is, not as `window.innerHeight` reports it.
 *
 * On a phone those two stop agreeing the moment a keyboard opens. `innerHeight`
 * is the layout viewport and does not change; `visualViewport.height` is what
 * is left above the keyboard. Anything sized off the first and anchored to the
 * bottom of the screen - the mail composer's send row is the case this was
 * written for - is then underneath it, and stays underneath it for exactly as
 * long as the operator is typing, which is exactly when they need it.
 *
 * `visualViewport` also moves rather than resizing when the page is pinched or
 * when iOS scrolls a focused field into view, so `offsetTop` is subtracted as
 * well: the height alone would be right while the top of it was somewhere else.
 *
 * Falls back to `window.innerWidth/innerHeight` where the API is missing, which
 * is the behaviour every caller had before this existed.
 */
import { useEffect, useState } from "react";

export interface ViewportSize {
  vw: number;
  vh: number;
}

function read(): ViewportSize {
  if (typeof window === "undefined") return { vw: 1280, vh: 800 };
  const vv = window.visualViewport;
  if (!vv) return { vw: window.innerWidth, vh: window.innerHeight };
  return { vw: vv.width, vh: vv.height + vv.offsetTop };
}

export function useViewportSize(): ViewportSize {
  const [size, setSize] = useState(read);

  useEffect(() => {
    const sync = () => setSize(read());
    sync();
    window.addEventListener("resize", sync);
    const vv = window.visualViewport;
    // Both events, and they are not the same one: `resize` is the keyboard
    // opening, `scroll` is iOS lifting the visual viewport to keep a focused
    // field in sight without changing its height.
    vv?.addEventListener("resize", sync);
    vv?.addEventListener("scroll", sync);
    return () => {
      window.removeEventListener("resize", sync);
      vv?.removeEventListener("resize", sync);
      vv?.removeEventListener("scroll", sync);
    };
  }, []);

  return size;
}
